import * as R from "ramda";
import {
  IClientPatch,
  IPatchAddAction,
  IPatchDeleteAction,
  IPatchInsertAction,
  IPatchSubstAction,
  PatchType,
} from "../../../../components/course-author/helpers/patches";
import { createPatchBase } from "../../../../components/course-author/helpers/patches/creators";
import { PreferredLocale } from "../../../../utils/locales";
import { ICompleteOrganization, IOrganizationAliasesElems, IOrganizationAliasesState } from "../../../types/common";

export function modifyOrganizationTitlePatch(
  organizationName: string,
  organization: ICompleteOrganization,
  userId: string,
  title: string
): IClientPatch<IPatchSubstAction<string>> {
  return {
    ...createPatchBase(userId, organization.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: organizationName,
        y: title,
      },
      position: "displayName",
    },
  };
}

export const makeDeleteOrganizationPatch = (
  userId: string,
  versionId: number
): IClientPatch<IPatchSubstAction<boolean>> => {
  return {
    ...createPatchBase(userId, versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: false,
        y: true,
      },
      position: "deleted",
    },
  };
};

export function modifyOrganizationSettingsLocalePatch(
  organization: ICompleteOrganization,
  userId: string,
  defaultLocale: PreferredLocale
): IClientPatch<IPatchSubstAction<string>> {
  return {
    ...createPatchBase(userId, organization.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: organization.defaultLocale,
        y: defaultLocale,
      },
      position: "defaultLocale",
    },
  };
}

export function modifyOrganizationSettingsAliasNamePatch(
  organization: ICompleteOrganization,
  userId: string,
  name: string
): IClientPatch<IPatchSubstAction<IOrganizationAliasesState> | IPatchAddAction<IOrganizationAliasesElems>> {
  const aliasState = organization.aliases.elems[0]?.state || undefined;

  if (aliasState) {
    return {
      ...createPatchBase(userId, organization.versionId + 1),
      patch: {
        action: {
          type: PatchType.subst,
          x: aliasState,
          y: {
            alias: { name, kind: "" },
            namespace: aliasState.namespace,
            tag: { tag: "granted-alias", content: null },
          },
        },
        position: "aliases.0.state",
      },
    };
  } else {
    return {
      ...createPatchBase(userId, organization.versionId + 1),
      patch: {
        action: {
          at: 0,
          item: {
            id: 0,
            state: {
              alias: { name, kind: "" },
              namespace: `org.${organization.organizationId}`,
              tag: { tag: "requested-new-alias", content: null },
            },
          },
          type: PatchType.add,
        },
        position: "aliases",
      },
    };
  }
}

type ReturnPatchAction<T> = IPatchInsertAction<T> | IPatchDeleteAction<T>;

const patchAdmins = (patchType: PatchType.ins | PatchType.del) => (
  organization: ICompleteOrganization,
  currentAccount: string,
  memberId: string
): IClientPatch<ReturnPatchAction<string>> => {
  return {
    ...createPatchBase(currentAccount, organization.versionId + 1),
    patch: {
      action: {
        elem: memberId,
        type: patchType,
      },
      position: "admins",
    },
  };
};

export const addNewAdminPatch = patchAdmins(PatchType.ins);
export const removeAdminPatch = patchAdmins(PatchType.del);

export type TogglePatch = (
  organization: ICompleteOrganization,
  userId: string
) => IClientPatch<IPatchSubstAction<boolean>>;

export const togglePatch = (attribute: R.Path): TogglePatch => (
  organization: ICompleteOrganization,
  userId: string
): IClientPatch<IPatchSubstAction<boolean>> => {
  const currentValue = R.path(attribute, organization) as boolean;
  return {
    ...createPatchBase(userId, organization.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: currentValue,
        y: !currentValue,
      },
      position: R.join(".", attribute),
    },
  };
};

export const toggleDeleteOrganizationPatch = togglePatch(["deleted"]);
export const toggleLearnerLogPatch = togglePatch(["storeLearnerLog"]);
export const toggleHideLMSNavBarPatch = togglePatch(["scormSettings", "hideNavBar"]);

export type ChangePatch = (
  organization: ICompleteOrganization,
  userId: string,
  newValue: string
) => IClientPatch<IPatchSubstAction<string>>;

export const changePatch = (attribute: R.Path): ChangePatch => (
  organization: ICompleteOrganization,
  userId: string,
  newValue: string
) => {
  return {
    ...createPatchBase(userId, organization.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: R.path(attribute, organization) as string,
        y: newValue,
      },
      position: R.join(".", attribute),
    },
  };
};
export const changeOwningOrganizationPatch = changePatch(["owner"]);
export const changeReleaseLinePatch = changePatch(["releaseLine"]);
export const changeLMSPatch = changePatch(["scormSettings", "lms"]);
export const changeSCORMVersionPatch = changePatch(["scormSettings", "scormVersion"]);
